<template>
  <section id="manage-customers">
    <page-header
      :breadcrumbs="breadcrumbs"
      :title="$dictionary.app.customers.index.title"
      header-col="5"
    >
      <v-col
        cols="12"
        md="8"
        lg="7"
        class="
          d-flex
          align-center
          justify-end
          flex-wrap flex-lg-nowrap
        "
      >
        <debounce-search-text-field
          prop-to-search="name"
          placeholder="Search Customers..."
          :lazy-search="getCustomerList"
          :sort-by="sortConfig.sortBy"
          :sort-order="sortConfig.sortOrder"
          @search-value="customerSearch = $event"
        />

        <v-btn
          color="secondary"
          outlined
          class="
            button
            add-user
            font-weight-bold
            btn-light-grey-border
            text--secondary
          "
          :to="$paths.createCustomer"
        >
          <v-icon class="pr-3 text--secondary"> mdi-plus </v-icon>
          Add Customer
        </v-btn>
        <div id="customers-csv-btn" class="ml-4">
          <csv-button
            :labels="labels"
            :data="csvFileCustomers"
            fileName="customers"
            :fields="csvFields"
            :loading="isLoading"
            :color="$appConfig.csvButton.color.secondary"
          />
        </div>
      </v-col>
    </page-header>
    <v-row class="d-flex justify-center pt-3" no-gutters>
      <v-col cols="12" class="pt-3">
        <customers-table
          :headers="headers"
          :customers="customers"
          :is-loading="isLoading"
          @get-records="getCustomerList"
          @current-items="setCsvCustomers"
        ></customers-table>
      </v-col>
    </v-row>
  </section>
</template>
<script>
import { mapActions } from "vuex";
import { getCustomers } from "@/services";
import { CsvButton } from "@/components/widgets";

import { csvFileLabels, hasProp } from "@/utils/common.utils";
import { CustomersTable } from "@/components/tables";
import { customersHeaders as headers } from "@/data/table-headers";

import { TitleMixin, SharedUtilsMixin } from "@/mixins";
import { PageHeader, DebounceSearchTextField } from "@/components/shared";

/**
 * Manage customers view
 */
export default {
  name: "ManageCustomers",
  /**
   *  View title
   */
  title: "Manage Customers",
  /**
   * -------------- Mixins -------------
   */
  mixins: [TitleMixin, SharedUtilsMixin],
  /**
   * ---------------- Components ------------------
   */
  components: {
    PageHeader,
    CsvButton,
    CustomersTable,
    DebounceSearchTextField,
  },
  /**
   * ---------------- Data properties ------------------
   */
  data() {
    return {
      isLoading: false,
      customers: [],
      headers,
      labels: csvFileLabels(headers),
      csvFileCustomers: [],
      customerParams: {},
      customerSearch: "",
    };
  },
  /**
   * ---------------- Computed properties ------------------
   */
  computed: {
    /**
     * Manage customers breadcrumbs
     * @type {Array}
     */
    breadcrumbs() {
      return [
        {
          title: this.$dictionary.app.customers.index.title,
          type: this.$appConfig.breadcrumbs.types.route,
        },
      ];
    },
    /**
     * Customers listing sorting configuration
     */
    sortConfig() {
      return this.getSortConfig("customerParams", "name");
    },
    /**
     * All customer list properties that are to be exported in csv
     * @type {Array} Aarry of all the csv properties
     */
    csvFields() {
      return Object.keys(this.labels);
    },
  },
  /**
   * ---------------- Methods ------------------
   */
  methods: {
    ...mapActions({
      setSnackbar: "ui/setSnackbar",
    }),
    /**
     * Fetched the customers list
     */
    async getCustomerList(params = this.customerParams) {
      try {
        this.isLoading = true;
        if (this.customerSearch && !hasProp(params, "q[name_cont]")) {
          params["q[name_cont]"] = this.customerSearch;
        }
        this.customerParams = params;

        const { data } = await getCustomers(params);
        this.customers = data || [];
      } catch (error) {
        this.setSnackbar({
          value: true,
          type: this.$appConfig.snackbar.snackbarTypes.error,
          message: this.$errorMessage(error),
        });
      } finally {
        this.isLoading = false;
      }
    },
    /**
     * Sets csv customer to be downloaded in csv file
     * @listens current-items
     */
    setCsvCustomers(customers) {
      this.csvFileCustomers = customers || [];
    },
  },
};
</script>
